import React from 'react';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { Link, MenuItem, Popover } from '@material-ui/core';
import { makeStyles } from 'design-system/styles';
import {
  INamedEntityRecognitionAttribute,
  INamedEntityRecognitionRelationshipDefinition,
  NamedEntityRecognitionAttributeType,
  SelectField,
} from '@scale/shared/types/params/namedentityrecognition';
import {
  INamedEntityRecognitionResponseAttribute,
  SelectFieldResponse,
  TextFieldResponse,
} from '@scale/shared/types/response/namedentityrecognition';
import _ from 'lodash';
import { Option, Select, IconButton } from 'design-system';
import {
  getLabelColorPalette,
  INamedEntityRecognitionLabel,
  IColorMap,
} from './namedEntityRecognitionLabels';
import NERLabelMenu from './NERLabelMenu';
import Fuse from 'fuse.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NERLinksMenu from './NERLinksMenu';
import { INamedEntityRecognitionRelationship } from './namedEntityRecognitionAnnotations';
import { TextArea } from '@scale/scaleui';

interface INERAnnotationPopoverProps {
  annotationId: string;
  horizontalOffset: number;
  verticalOffset: number;
  label: INamedEntityRecognitionLabel;
  labels: INamedEntityRecognitionLabel[];
  labelColormap: IColorMap;
  labelSearchIndex: Fuse<INamedEntityRecognitionLabel>;
  relationships: INamedEntityRecognitionRelationship[];
  defaultAttributeFields?: Record<string, INamedEntityRecognitionAttribute>;
  relationshipDefinitions: INamedEntityRecognitionRelationshipDefinition[];
  labelAttributes?: { [key: string]: INamedEntityRecognitionAttribute };
  attributeValues?: { [key: string]: INamedEntityRecognitionResponseAttribute };
  hiddenRelationships: { [id: string]: boolean };
  toggleRelationshipVisibility: (id: string) => void;
  onChangeLabel: (label: INamedEntityRecognitionLabel) => void;
  onChangeAttributeValue: (key: string, value: INamedEntityRecognitionResponseAttribute) => void;
  onClose: () => void;
  onEnterLinkingMode: () => void;
  onRemoveRelationship: (relationshipId: string) => void;
  removeFocusedAnnotation: () => void;
  checkRequiredAttributeExist: (key: string) => boolean;
}

const NERAnnotationPopover: React.ForwardRefRenderFunction<
  HTMLElement,
  INERAnnotationPopoverProps
> = (
  {
    annotationId,
    horizontalOffset,
    verticalOffset,
    label,
    labels,
    labelColormap,
    labelSearchIndex,
    relationships,
    defaultAttributeFields,
    relationshipDefinitions,
    labelAttributes = {},
    attributeValues = {},
    onChangeLabel,
    onChangeAttributeValue,
    onClose,
    onEnterLinkingMode,
    onRemoveRelationship,
    hiddenRelationships,
    toggleRelationshipVisibility,
    removeFocusedAnnotation,
    checkRequiredAttributeExist,
  },
  containerRef: $FIXME,
) => {
  const classes = useNERAnnotationPopoverClasses();

  const [isEditing, setIsEditing] = React.useState(false);
  const [isLinksMenuOpen, setIsLinksMenuOpen] = React.useState(false);
  const linksMenuAnchorEl = React.useRef<HTMLLIElement | null>(null);
  const [isRequired, setIsRequired] = React.useState(false);
  const onLabelSelect = (label: INamedEntityRecognitionLabel) => {
    onChangeLabel(label);
    setIsEditing(false);
  };

  const onCloseCondition = () => {
    const keys = Object.keys(labelAttributes);
    const filteredKeys = keys.filter(key => labelAttributes[key]?.required === true);
    const exists = filteredKeys.map(key => checkRequiredAttributeExist(key));
    const check = exists.filter(x => x === false);
    if (check.length === 0) {
      setIsRequired(false);
      onClose();
    }
    setIsRequired(true);
  };

  const onSelectChange = (key: string) => (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    if ((labelAttributes[key] as SelectField)?.options.map(o => o.value).includes(value)) {
      onChangeAttributeValue(key, { selected: value });
    }
  };

  const labelColorPalette = getLabelColorPalette(label.name, labelColormap);

  return (
    containerRef.current && (
      <Popover
        anchorEl={containerRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: horizontalOffset, vertical: verticalOffset }}
        open={true}
        onClose={onCloseCondition}
      >
        <div className={classes.popoverContent}>
          {isEditing ? (
            <NERLabelMenu
              labels={labels}
              labelColormap={labelColormap}
              onSelect={onLabelSelect}
              onResize={() => {}}
              labelSearchIndex={labelSearchIndex}
            />
          ) : (
            <>
              <div className={classes.annotationHeader}>
                <div
                  className={classes.annotationHeaderIcon}
                  style={{
                    backgroundColor: labelColorPalette.listItemBackground,
                  }}
                />
                <div className={classes.annotationHeaderLabel}>
                  {label.display_name ?? label.name}
                </div>
                <Link
                  classes={{ root: classes.annotationHeaderEdit }}
                  component="button"
                  type="button"
                  color="primary"
                  underline="none"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Link>
                <IconButton
                  size="small"
                  onClick={removeFocusedAnnotation}
                  className={classes.closeButton}
                >
                  <FontAwesomeIcon icon="trash" />
                </IconButton>
              </div>
              {relationshipDefinitions.length ? (
                relationships.length === 0 ? (
                  <MenuItem className={classes.addLinkButton} onClick={onEnterLinkingMode}>
                    + Add link
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem
                      className={classes.linksMenuButton}
                      onClick={() => setIsLinksMenuOpen(true)}
                      selected={isLinksMenuOpen}
                      ref={linksMenuAnchorEl}
                    >
                      Links ({relationships.length})
                      <div className={classes.linksIcon}>
                        <FontAwesomeIcon icon="chevron-right" />
                      </div>
                    </MenuItem>
                    {isLinksMenuOpen && (
                      <Popover
                        open={true}
                        anchorEl={linksMenuAnchorEl.current}
                        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                        onClose={() => setIsLinksMenuOpen(false)}
                        elevation={9}
                        classes={{ root: classes.linksMenuPopover }}
                      >
                        <NERLinksMenu
                          annotationId={annotationId}
                          relationships={relationships}
                          labelColormap={labelColormap}
                          onEnterLinkingMode={onEnterLinkingMode}
                          onRemoveRelationship={onRemoveRelationship}
                          hiddenRelationships={hiddenRelationships}
                          toggleRelationshipVisibility={toggleRelationshipVisibility}
                        />
                      </Popover>
                    )}
                  </>
                )
              ) : null}
              {defaultAttributeFields && (
                <>
                  {_.map(defaultAttributeFields, (field, key) => (
                    <div className={classes.attribute} key={key}>
                      <div className={classes.attributeTitle}>{field.display_name}</div>
                      <TextArea
                        id={key}
                        name={field.display_name}
                        value={(attributeValues[key] as TextFieldResponse)?.text ?? ''}
                        onChange={e => onChangeAttributeValue(key, { text: e.target.value })}
                        description={field.description}
                      />
                    </div>
                  ))}
                </>
              )}
              {_.map(labelAttributes, (attribute, key) => (
                <div className={classes.attribute} key={key}>
                  {isRequired && labelAttributes[key].required && (
                    <span style={{ color: 'red' }}>Fill required attribute</span>
                  )}
                  <div className={classes.attributeTitle}>{attribute.display_name ?? key}</div>
                  {attribute.type === NamedEntityRecognitionAttributeType.Select && (
                    <Select
                      value={(attributeValues[key] as SelectFieldResponse)?.selected ?? ''}
                      onChange={onSelectChange(key)}
                    >
                      {_.map(attribute.options, option => (
                        <Option key={option.value} value={option.value}>
                          {option.display_name ?? option.value}
                        </Option>
                      ))}
                    </Select>
                  )}
                  {attribute.type === NamedEntityRecognitionAttributeType.Text && (
                    <TextArea
                      id={key}
                      name={key}
                      value={(attributeValues[key] as TextFieldResponse)?.text ?? ''}
                      onChange={e => onChangeAttributeValue(key, { text: e.target.value })}
                      description={attribute.description}
                    />
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </Popover>
    )
  );
};

const useNERAnnotationPopoverClasses = makeStyles({
  popoverContent: {
    maxHeight: 340,
    minWidth: 200,
    paddingBottom: 8,
    overflowY: 'scroll',
  },

  annotationHeader: {
    display: 'flex',
    margin: 16,
    marginBottom: 8,
    alignItems: 'center',
  },

  annotationHeaderIcon: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },

  annotationHeaderLabel: {
    marginInlineStart: '8px',
    marginInlineEnd: '16px',
    fontSize: '14px',
  },

  annotationHeaderEdit: {
    marginLeft: 'auto',
    justifyContent: 'flex-end',
    fontSize: '14px',
    color: '#2b99d8',
  },

  addLinkButton: {
    color: '#000',
    fontWeight: 500,
    fontSize: 14,
  },

  linksMenuButton: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
  },

  linksIcon: {
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    color: '#000',
    opacity: 0.5,
    fontSize: 12,
  },

  linksMenuPopover: {
    pointerEvents: 'none',
  },

  linksMenu: {
    pointerEvents: 'all',
  },

  attribute: {
    padding: 16,
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
    marginTop: 16,
  },

  attributeTitle: {
    fontSize: 12,
    fontWeight: 500,
    color: '#808080',
    textTransform: 'uppercase',
    lineHeight: '16px',
    letterSpacing: '0.5px',
    paddingBottom: 4,
  },

  closeButton: {
    marginLeft: 8,
    color: 'grey',
    '&:hover': {
      color: 'black',
    },
  },
});

export default React.forwardRef(NERAnnotationPopover);
